import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth, useLanguage, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ImageUploader } from "@/components/ImageUploader";
import { US_STATES } from "@/lib/listings";
import { toast } from "sonner";
import { Plus, Edit, Trash2, X, Save, Star, Sparkles } from "lucide-react";

const SECTIONS = [
  { value: "tourist", label_ar: "سياحي", label_en: "Tourist" },
  { value: "entertainment", label_ar: "ترفيهي", label_en: "Entertainment" },
  { value: "arabic", label_ar: "عربي", label_en: "Arabic" }
];

const CATEGORIES = [
  "landmark", "park", "beach", "theme_park", "mall", "cinema", "gym", "arcade",
  "event", "night", "restaurant", "halal_food", "mosque", "supermarket", "hookah", "arab_community"
];

const blank = {
  name: "", name_ar: "", section: "tourist", category: "landmark",
  description: "", address: "", state: "", city: "", zip_code: "",
  lat: "", lng: "", images: [], ticket_price: "", opening_hours: "", rating: "",
  family_friendly: false, parking_available: false, nearby_transit: "",
  phone: "", website: "", video_url: "", featured: false,
  best_time_to_visit: "", estimated_cost: "", social_instagram: "", social_facebook: "",
  transportation_tips: "", arabic_tip: ""
};

export default function AdminAttractions() {
  const { token } = useAuth();
  const { t, language } = useLanguage();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | attraction_id
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/attractions`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(res.data);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const startNew = () => { setForm(blank); setEditing("new"); };
  const startEdit = (a) => {
    setForm({
      ...blank, ...a,
      lat: a.lat ?? "", lng: a.lng ?? "", rating: a.rating ?? "",
      images: a.images || []
    });
    setEditing(a.attraction_id);
  };
  const cancel = () => { setEditing(null); setForm(blank); };

  const save = async () => {
    if (!form.name || !form.section || !form.category || !form.address || !form.state || !form.city || !form.zip_code || !form.description) {
      toast.error(t("يرجى تعبئة الحقول المطلوبة", "Please fill required fields"));
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        lat: form.lat === "" ? null : parseFloat(form.lat),
        lng: form.lng === "" ? null : parseFloat(form.lng),
        rating: form.rating === "" ? null : parseFloat(form.rating)
      };
      if (editing === "new") {
        await axios.post(`${API}/admin/attractions`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(t("تمت الإضافة", "Created"));
      } else {
        await axios.put(`${API}/admin/attractions/${editing}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(t("تم التحديث", "Updated"));
      }
      cancel();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("فشلت العملية", "Operation failed"));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("تأكيد الحذف؟", "Confirm delete?"))) return;
    try {
      await axios.delete(`${API}/admin/attractions/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("تم الحذف", "Deleted"));
      load();
    } catch (e) { toast.error(t("فشل الحذف", "Delete failed")); }
  };

  const inputCls = "w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-white focus:border-yellow-500 outline-none text-sm";

  return (
    <div className="space-y-4" data-testid="admin-attractions">
      <div className="flex justify-between items-center">
        <h3 className="font-bold text-white flex items-center gap-2">
          <Sparkles className="text-yellow-500" size={20} /> {t("الأماكن والسياحة", "Attractions & Tourism")}
        </h3>
        {!editing && (
          <Button onClick={startNew} className="btn-primary" data-testid="new-attraction-btn">
            <Plus size={16} className="ml-2" /> {t("إضافة مكان", "Add place")}
          </Button>
        )}
      </div>

      {editing && (
        <div className="glass-card rounded-2xl p-5 space-y-4" data-testid="attraction-form">
          <div className="flex justify-between items-center">
            <h4 className="font-bold text-white">{editing === "new" ? t("مكان جديد", "New place") : t("تعديل مكان", "Edit place")}</h4>
            <button onClick={cancel} className="text-slate-400 hover:text-white"><X size={20} /></button>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <input className={inputCls} placeholder={t("الاسم بالإنجليزية *", "Name (EN) *")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="attr-name" />
            <input className={inputCls} placeholder={t("الاسم بالعربية", "Name (AR)")} value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} />
            <select className={inputCls} value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })}>
              {SECTIONS.map((s) => <option key={s.value} value={s.value}>{language === "ar" ? s.label_ar : s.label_en}</option>)}
            </select>
            <select className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input className={inputCls} placeholder={t("العنوان الكامل *", "Full address *")} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            <input className={inputCls} placeholder={t("ZIP *", "ZIP *")} value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} />
            <select className={inputCls} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })}>
              <option value="">{t("الولاية *", "State *")}</option>
              {US_STATES.map((s) => <option key={s.code} value={s.code}>{language === "ar" ? s.name_ar : s.name_en}</option>)}
            </select>
            <input className={inputCls} placeholder={t("المدينة *", "City *")} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            <input className={inputCls} placeholder={t("Latitude (اختياري)", "Latitude")} value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} />
            <input className={inputCls} placeholder={t("Longitude (اختياري)", "Longitude")} value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} />
            <input className={inputCls} placeholder={t("سعر التذكرة (Free / $25)", "Ticket price")} value={form.ticket_price} onChange={(e) => setForm({ ...form, ticket_price: e.target.value })} />
            <input className={inputCls} placeholder={t("أوقات العمل", "Opening hours")} value={form.opening_hours} onChange={(e) => setForm({ ...form, opening_hours: e.target.value })} />
            <input className={inputCls} placeholder={t("التقييم (0-5)", "Rating 0-5")} value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} />
            <input className={inputCls} placeholder={t("المواصلات القريبة", "Nearby transit")} value={form.nearby_transit} onChange={(e) => setForm({ ...form, nearby_transit: e.target.value })} />
            <input className={inputCls} placeholder={t("الهاتف", "Phone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <input className={inputCls} placeholder={t("الموقع الرسمي", "Website")} value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
            <input className={inputCls} placeholder={t("رابط فيديو يوتيوب", "YouTube URL")} value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} data-testid="attr-video" />
            <input className={inputCls} placeholder={t("أفضل وقت للزيارة", "Best time to visit")} value={form.best_time_to_visit} onChange={(e) => setForm({ ...form, best_time_to_visit: e.target.value })} />
            <input className={inputCls} placeholder={t("التكلفة التقديرية", "Estimated cost")} value={form.estimated_cost} onChange={(e) => setForm({ ...form, estimated_cost: e.target.value })} />
            <input className={inputCls} placeholder={t("رابط Instagram", "Instagram URL")} value={form.social_instagram} onChange={(e) => setForm({ ...form, social_instagram: e.target.value })} />
            <input className={inputCls} placeholder={t("رابط Facebook", "Facebook URL")} value={form.social_facebook} onChange={(e) => setForm({ ...form, social_facebook: e.target.value })} />
            <input className={inputCls} placeholder={t("نصائح التنقل", "Transportation tips")} value={form.transportation_tips} onChange={(e) => setForm({ ...form, transportation_tips: e.target.value })} />
            <input className={inputCls} placeholder={t("نصيحة عربية", "Arabic tip")} value={form.arabic_tip} onChange={(e) => setForm({ ...form, arabic_tip: e.target.value })} />
          </div>
          <textarea className={inputCls} rows={3} placeholder={t("الوصف *", "Description *")} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="attr-description" />
          <div className="flex gap-4 flex-wrap">
            {[
              { k: "family_friendly", ar: "للعائلات", en: "Family friendly" },
              { k: "parking_available", ar: "موقف سيارات", en: "Parking" },
              { k: "featured", ar: "مميز", en: "Featured" }
            ].map((c) => (
              <label key={c.k} className="flex items-center gap-2 text-white text-sm">
                <input type="checkbox" checked={form[c.k]} onChange={(e) => setForm({ ...form, [c.k]: e.target.checked })} />
                {language === "ar" ? c.ar : c.en}
              </label>
            ))}
          </div>
          <ImageUploader value={form.images} onChange={(imgs) => setForm({ ...form, images: imgs })} max={6} />
          <div className="flex gap-2 justify-end">
            <Button onClick={cancel} variant="ghost" className="text-slate-300">{t("إلغاء", "Cancel")}</Button>
            <Button onClick={save} disabled={busy} className="btn-primary" data-testid="save-attraction-btn">
              <Save className="ml-2" size={16} /> {busy ? t("جاري الحفظ...", "Saving...") : t("حفظ", "Save")}
            </Button>
          </div>
        </div>
      )}

      <div className="glass-card rounded-2xl divide-y divide-slate-800">
        {items.length === 0 ? (
          <div className="p-8 text-center text-slate-400">{t("لا توجد أماكن", "No places")}</div>
        ) : items.map((a) => (
          <div key={a.attraction_id} className="p-4 flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-semibold text-white truncate">{a.name}</h4>
                {a.featured && <Badge className="bg-yellow-500 text-black"><Star size={10} className="ml-1" />{t("مميز", "Feat.")}</Badge>}
              </div>
              <p className="text-xs text-slate-400">{a.city}, {a.state} {a.zip_code} · <span className="capitalize">{a.section}/{a.category}</span></p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <Button size="sm" variant="outline" onClick={() => startEdit(a)} className="border-slate-700 text-white" data-testid={`edit-attr-${a.attraction_id}`}>
                <Edit size={14} />
              </Button>
              <Button size="sm" onClick={() => remove(a.attraction_id)} className="bg-red-500/20 text-red-400 hover:bg-red-500/30" data-testid={`delete-attr-${a.attraction_id}`}>
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
